import { isMapStyleReady } from '../mapcore.js';
// Radiosonda siciliana LIVE: MQTT sul server, payload canonico e limitato al browser.
const ENDPOINT = '/api/sonde-live.php';
const REFRESH_MS = 2000;
const TRACKING_STALE_MS = 5 * 60 * 1000;
const SOURCE = 'ws-sondehub-track-src';
const LAYER = 'ws-sondehub-track';

const state = {
    map: null,
    enabled: false,
    timer: 0,
    marker: null,
    popup: null,
    data: null,
    animation: 0,
    firstFix: true,
    fetching: false,
    lastFreshAt: 0,
};

const esc = value => String(value ?? '').replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[c]));

const number = (value, digits = 1, unit = '') => {
    const n = Number(value);
    return Number.isFinite(n) ? `${n.toFixed(digits)}${unit}` : 'n.d.';
};

function ensureTrack() {
    const map = state.map;
    if (!map || map.getSource(SOURCE) || !isMapStyleReady(map)) return;
    map.addSource(SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({
        id: LAYER,
        type: 'line',
        source: SOURCE,
        paint: {
            'line-color': '#e11d8a',
            'line-width': 3,
            'line-opacity': 0.9,
            'line-dasharray': [1.4, 1.2],
        },
    }, map.getLayer('ws-stations-symbols') ? 'ws-stations-symbols' : undefined);
}

function renderTrack(data) {
    ensureTrack();
    const source = state.map?.getSource(SOURCE);
    if (!source) return;
    const coordinates = (data.track || [])
        .filter(row => Array.isArray(row) && Number.isFinite(Number(row[0])) && Number.isFinite(Number(row[1])))
        .map(row => [Number(row[0]), Number(row[1])]);
    source.setData({
        type: 'FeatureCollection',
        features: coordinates.length > 1 ? [{
            type: 'Feature',
            properties: { serial: data.serial },
            geometry: { type: 'LineString', coordinates },
        }] : [],
    });
}

function popupHtml(data) {
    const p = data.position || {};
    const updated = data.updated_at_utc
        ? new Intl.DateTimeFormat('it-IT', { dateStyle: 'short', timeStyle: 'medium' }).format(new Date(data.updated_at_utc))
        : 'n.d.';
    return `<article class="ws-sonde-popup">
        <header><span>🎈</span><div><small>RADIOSONDA LIVE</small><h3>${esc(data.subtype || '')} ${esc(data.serial || '')}</h3></div></header>
        <dl>
            <div><dt>Fase</dt><dd>${esc(data.phase || 'n.d.')}</dd></div>
            <div><dt>Quota</dt><dd>${number(p.altitude_m, 0, ' m')}</dd></div>
            <div><dt>Temperatura</dt><dd>${number(data.temperature_c, 1, ' °C')}</dd></div>
            <div><dt>Umidità</dt><dd>${number(data.humidity_pct, 1, '%')}</dd></div>
            <div><dt>Salita/discesa</dt><dd>${number(data.vertical_speed_ms, 1, ' m/s')}</dd></div>
            <div><dt>Velocità</dt><dd>${number(Number(data.horizontal_speed_ms) * 3.6, 1, ' km/h')}</dd></div>
        </dl>
        <p class="ws-sonde-popup__time">Aggiornata: ${esc(updated)}</p>
        <a href="${esc(data.sounding_url || 'https://sounding.weathersicily.it/')}" target="_blank" rel="noopener">Apri radiosondaggio completo →</a>
    </article>`;
}

function openPopup() {
    const data = state.data;
    const p = data?.position || {};
    if (!Number.isFinite(Number(p.lon)) || !Number.isFinite(Number(p.lat))) return;
    state.popup?.remove();
    state.popup = new maplibregl.Popup({ offset: 30, className: 'ws-sonde-popup-shell', maxWidth: '330px' })
        .setLngLat([Number(p.lon), Number(p.lat)])
        .setHTML(popupHtml(data))
        .addTo(state.map);
}

function makeMarker() {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = 'ws-sonde-marker';
    el.title = 'Apri telemetria radiosonda LIVE';
    el.setAttribute('aria-label', 'Radiosonda LIVE');
    el.innerHTML = '<span class="ws-sonde-marker__balloon">🎈</span><b class="ws-sonde-marker__alt"></b><i>LIVE</i>';
    el.addEventListener('click', event => { event.stopPropagation(); openPopup(); });
    return el;
}

function animateMarker(lon, lat) {
    if (!state.marker) {
        state.marker = new maplibregl.Marker({ element: makeMarker(), anchor: 'bottom' })
            .setLngLat([lon, lat])
            .addTo(state.map);
        return;
    }
    cancelAnimationFrame(state.animation);
    const start = state.marker.getLngLat();
    const started = performance.now();
    const duration = 1200;
    const step = now => {
        const t = Math.min(1, (now - started) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        const current = [start.lng + (lon - start.lng) * eased, start.lat + (lat - start.lat) * eased];
        state.marker.setLngLat(current);
        if (state.popup?.isOpen()) state.popup.setLngLat(current);
        if (t < 1) state.animation = requestAnimationFrame(step);
    };
    state.animation = requestAnimationFrame(step);
}

function clear() {
    cancelAnimationFrame(state.animation);
    state.marker?.remove();
    state.popup?.remove();
    state.marker = null;
    state.popup = null;
    state.data = null;
    const source = state.map?.getSource(SOURCE);
    if (source) source.setData({ type: 'FeatureCollection', features: [] });
}

function schedule() {
    clearTimeout(state.timer);
    if (state.enabled) state.timer = window.setTimeout(refresh, document.hidden ? 10000 : REFRESH_MS);
}

async function refresh() {
    if (!state.enabled || state.fetching) return schedule();
    state.fetching = true;
    try {
        const response = await fetch(ENDPOINT, { cache: 'no-store', headers: { Accept: 'application/json' } });
        const data = await response.json();
        if (!response.ok || !data.available) {
            clear();
            state.lastFreshAt = 0;
            return;
        }
        state.lastFreshAt = Date.now();
        state.data = data;
        const p = data.position || {};
        const lon = Number(p.lon), lat = Number(p.lat);
        if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
        renderTrack(data);
        animateMarker(lon, lat);
        const altitude = state.marker?.getElement()?.querySelector('.ws-sonde-marker__alt');
        if (altitude) altitude.textContent = number(p.altitude_m, 0, ' m');
        if (state.popup?.isOpen()) state.popup.setHTML(popupHtml(data));
        if (state.firstFix) {
            state.firstFix = false;
            // A scala Sicilia pochi secondi di deriva valgono meno di un pixel:
            // lo zoom iniziale segue il volo abbastanza da renderne percepibile
            // lo spostamento, senza bloccare pan o zoom manuali.
            state.map.easeTo({ center: [lon, lat], zoom: Math.max(state.map.getZoom(), 11.5), duration: 900 });
        }
    } catch {
        // Un errore breve non fa lampeggiare il marker; oltre la stessa finestra
        // ammessa dal server la posizione non viene mai lasciata congelata.
        if (!state.lastFreshAt || Date.now() - state.lastFreshAt > TRACKING_STALE_MS) clear();
    } finally {
        state.fetching = false;
        schedule();
    }
}

export function initSondehub(map) {
    state.map = map;
    document.addEventListener('visibilitychange', () => {
        if (state.enabled && !document.hidden) void refresh();
    });
}

export function setSondehubEnabled(enabled) {
    state.enabled = !!enabled;
    clearTimeout(state.timer);
    if (!state.enabled) {
        state.firstFix = true;
        clear();
        return;
    }
    void refresh();
}
